import type { SmmMetric, SmmPlatform, SmmProductConfig } from '#bv/data/products'

export type { SmmMetric, SmmPlatform } from '#bv/data/products'

export type SmmTargetInputConfig = {
  label: string
  placeholder: string
  hint: string
}

const PLATFORM_HOSTS: Record<SmmPlatform, string[]> = {
  tiktok: ['tiktok.com', 'vm.tiktok.com'],
  instagram: ['instagram.com', 'www.instagram.com'],
  youtube: ['youtube.com', 'www.youtube.com', 'youtu.be'],
  twitter: ['twitter.com', 'x.com', 'www.twitter.com', 'www.x.com'],
  facebook: ['facebook.com', 'www.facebook.com', 'fb.com', 'm.facebook.com'],
  telegram: ['t.me', 'telegram.me'],
  spotify: ['open.spotify.com', 'spotify.com'],
  twitch: ['twitch.tv', 'www.twitch.tv'],
  generic: [],
}

export function smmPlatformLabel(platform: SmmPlatform): string {
  switch (platform) {
    case 'tiktok':
      return 'TikTok'
    case 'instagram':
      return 'Instagram'
    case 'youtube':
      return 'YouTube'
    case 'twitter':
      return 'X (Twitter)'
    case 'facebook':
      return 'Facebook'
    case 'telegram':
      return 'Telegram'
    case 'spotify':
      return 'Spotify'
    case 'twitch':
      return 'Twitch'
    default:
      return 'Profile / link'
  }
}

export function smmMetricLabel(metric: SmmMetric, plural = true): string {
  const map: Record<SmmMetric, [string, string]> = {
    followers: ['follower', 'followers'],
    subscribers: ['subscriber', 'subscribers'],
    likes: ['like', 'likes'],
    views: ['view', 'views'],
    members: ['member', 'members'],
    comments: ['comment', 'comments'],
  }
  const pair = map[metric] ?? ['unit', 'units']
  return plural ? pair[1] : pair[0]
}

export function smmTargetInputForConfig(cfg: SmmProductConfig): SmmTargetInputConfig {
  if (cfg.targetInput) return cfg.targetInput
  const platform = smmPlatformLabel(cfg.platform)
  switch (cfg.platform) {
    case 'tiktok':
      return {
        label: 'TikTok profile',
        placeholder: '@username or https://tiktok.com/@username',
        hint: 'Public profile required until delivery completes.',
      }
    case 'instagram':
      return {
        label: 'Instagram profile',
        placeholder: '@username or https://instagram.com/username',
        hint: 'Public account required. Do not change username while processing.',
      }
    case 'youtube':
      return {
        label: 'YouTube channel',
        placeholder: '@channel or https://youtube.com/@channel',
        hint: 'Channel must be public. Paste the channel URL or @handle.',
      }
    case 'twitter':
      return {
        label: 'X profile',
        placeholder: '@username or https://x.com/username',
        hint: 'Public profile required until the order completes.',
      }
    case 'telegram':
      return {
        label: 'Telegram link',
        placeholder: 'https://t.me/yourchannel or @username',
        hint: 'Use a public channel or group invite link as required by the service.',
      }
    default:
      return {
        label: `${platform} link`,
        placeholder: 'https://…',
        hint: 'Paste the exact profile or post URL from your panel instructions.',
      }
  }
}

function hostAllowed(host: string, platform: SmmPlatform): boolean {
  const h = host.replace(/^www\./i, '').toLowerCase()
  if (platform === 'generic') return true
  return PLATFORM_HOSTS[platform].some((allowed) => h === allowed.replace(/^www\./i, '') || h.endsWith(`.${allowed.replace(/^www\./i, '')}`))
}

function normalizeUrl(raw: string, platform: SmmPlatform): string | null {
  const input = raw.trim()
  if (!/^https?:\/\//i.test(input)) return null
  try {
    const u = new URL(input)
    if (!hostAllowed(u.hostname, platform)) return null
    return u.toString()
  } catch {
    return null
  }
}

function normalizeUsername(raw: string, platform: SmmPlatform): string | null {
  const user = raw.replace(/^@+/, '').trim()
  if (!user) return null

  switch (platform) {
    case 'tiktok':
      if (!/^[a-zA-Z0-9._]{2,24}$/.test(user)) return null
      return `https://www.tiktok.com/@${user}`
    case 'instagram':
      if (!/^[a-zA-Z0-9._]{1,30}$/.test(user)) return null
      return `https://www.instagram.com/${user}`
    case 'youtube':
      if (!/^[a-zA-Z0-9._-]{3,30}$/.test(user)) return null
      return `https://www.youtube.com/@${user}`
    case 'twitter':
      if (!/^[a-zA-Z0-9_]{1,15}$/.test(user)) return null
      return `https://x.com/${user}`
    case 'telegram':
      if (!/^[a-zA-Z0-9_]{4,32}$/.test(user)) return null
      return `https://t.me/${user}`
    case 'facebook':
      if (!/^[a-zA-Z0-9.]{2,50}$/.test(user)) return null
      return `https://www.facebook.com/${user}`
    case 'twitch':
      if (!/^[a-zA-Z0-9_]{4,25}$/.test(user)) return null
      return `https://www.twitch.tv/${user}`
    default:
      return null
  }
}

/** Normalize buyer input to a canonical URL for the SMM supplier `link` field. */
export function normalizeSmmTarget(raw: string, platform: SmmPlatform): string | null {
  const input = raw.trim()
  if (!input) return null

  const fromUrl = normalizeUrl(input, platform)
  if (fromUrl) return fromUrl

  if (platform === 'generic') {
    if (input.length >= 10 && input.length <= 500 && /^https?:\/\//i.test(input)) return input
    return null
  }

  return normalizeUsername(input.startsWith('@') ? input : `@${input}`, platform)
}

export function isValidSmmTarget(raw: string, platform: SmmPlatform): boolean {
  return normalizeSmmTarget(raw, platform) != null
}

export function smmTargetValidationMessage(raw: string, platform: SmmPlatform): string | null {
  const t = raw.trim()
  if (!t) return `Enter your ${smmPlatformLabel(platform)} username or profile link.`
  if (!isValidSmmTarget(t, platform)) {
    if (platform === 'generic') {
      return 'Paste a valid https:// profile or post link (10–500 characters).'
    }
    return `Use a valid ${smmPlatformLabel(platform)} profile link or @username.`
  }
  return null
}

export function normalizeSmmTargetForProduct(raw: string, cfg: SmmProductConfig): string | null {
  return normalizeSmmTarget(raw, cfg.platform)
}

export function smmTargetValidationMessageForProduct(raw: string, cfg: SmmProductConfig): string | null {
  return smmTargetValidationMessage(raw, cfg.platform)
}

export function formatSmmTargetForBuyer(target: string, platform: SmmPlatform): string {
  const t = target.trim()
  if (!t) return '—'
  if (t.startsWith('http')) return t
  if (platform === 'generic') return t
  if (t.startsWith('@')) return t
  return `@${t.replace(/^@/, '')}`
}

/** Default Raja catalog filter keywords per platform + metric (override per product in `servicePicker`). */
export function defaultServicePickerKeywords(
  platform: SmmPlatform,
  metric: SmmMetric,
): { nameKeywords: string[]; categoryKeywords: string[] } {
  const metricWord = metric === 'subscribers' ? 'subscriber' : metric === 'members' ? 'member' : metric
  const platformWord = platform === 'twitter' ? 'twitter' : platform
  return {
    nameKeywords: [platformWord, metricWord],
    categoryKeywords: [platformWord],
  }
}
